import { useState, useLayoutEffect, useCallback } from 'react';

import { computeAnchorPosition } from '#lib/anchorPosition';

/**
 * Keeps a portalled panel positioned against its trigger while open.
 *
 * Repositions on scroll and resize. The scroll listener runs in the capture phase because the
 * element that actually scrolls is often an inner container (a table body), not the window.
 *
 * @param {{current: HTMLElement|null}} triggerRef
 * @param {{current: HTMLElement|null}} panelRef - measured to clamp the panel inside the viewport
 * @param {boolean} open
 * @param {'start'|'end'|'center'|'stretch'} align
 * @returns {Object|null} inline style for the panel, or null before the first measure
 */
export function useAnchoredPosition(triggerRef, panelRef, open, align = 'start') {
	const [style, setStyle] = useState(null);

	const place = useCallback(() => {
		const trigger = triggerRef.current;
		if (!trigger) return;
		const width = panelRef.current?.offsetWidth ?? null;
		setStyle(computeAnchorPosition(trigger.getBoundingClientRect(), width, align));
	}, [triggerRef, panelRef, align]);

	useLayoutEffect(() => {
		if (!open) {
			setStyle(null);
			return undefined;
		}
		place();
		window.addEventListener('scroll', place, true);
		window.addEventListener('resize', place);
		return () => {
			window.removeEventListener('scroll', place, true);
			window.removeEventListener('resize', place);
		};
	}, [open, place]);

	return style;
}
